import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from 'react-i18next';
import { ATTENDANCE_DECISIONS } from "./attendanceDraft";

const getInitials = student => (
  `${(student.firstName || "").charAt(0)}${(student.lastName || "").charAt(0)}`.toUpperCase()
);

const getStudentName = student => (
  `${student.firstName || ""} ${student.lastName || ""}`.trim() || student.id
);

const formatNoticeDuration = (noticeMinutes, tr) => {
  if (noticeMinutes === null || noticeMinutes === undefined || Number.isNaN(noticeMinutes)) {
    return "";
  }

  if (noticeMinutes < 1) {
    return tr('manage:attendance.lessThanMinute');
  }

  const days = Math.floor(noticeMinutes / (24 * 60));
  const hours = Math.floor((noticeMinutes % (24 * 60)) / 60);
  const minutes = noticeMinutes % 60;
  const parts = [];

  if (days) parts.push(tr('manage:attendance.days', { count: days }));
  if (hours) parts.push(tr('manage:attendance.hours', { count: hours }));
  if (minutes) parts.push(tr('manage:attendance.minutes', { count: minutes }));

  return tr('manage:attendance.beforeLesson', { duration: parts.slice(0, 2).join(' ') });
};

const getNoticeMinutes = (decision, lessonDetails) => {
  const cancellation = decision.cancellation || {};

  if (cancellation.reportedAt && lessonDetails.date && lessonDetails.startTime) {
    const lessonStart = new Date(`${lessonDetails.date}T${lessonDetails.startTime}`);
    const reportedAt = new Date(cancellation.reportedAt);

    if (!Number.isNaN(lessonStart.getTime()) && !Number.isNaN(reportedAt.getTime())) {
      return Math.max(0, Math.round((lessonStart.getTime() - reportedAt.getTime()) / 60000));
    }
  }

  if (cancellation.cancellationNoticeHours !== undefined && cancellation.cancellationNoticeHours !== null) {
    return Number(cancellation.cancellationNoticeHours) * 60;
  }

  return null;
};

const getCancellationLabel = (decision, lessonDetails, tr) => {
  const notice = formatNoticeDuration(getNoticeMinutes(decision, lessonDetails), tr);
  const suffix = notice ? ` · ${notice}` : "";

  if (decision.decision === ATTENDANCE_DECISIONS.CANCELLED_ON_TIME) {
    return `${tr('manage:attendance.cancelledOnTime')}${suffix}`;
  }

  if (decision.decision === ATTENDANCE_DECISIONS.CANCELLED_LATE) {
    return `${tr('manage:attendance.cancelledLate')}${suffix}`;
  }

  return "";
};

const isCancelledDecision = decision => (
  decision.decision === ATTENDANCE_DECISIONS.CANCELLED_ON_TIME ||
  decision.decision === ATTENDANCE_DECISIONS.CANCELLED_LATE
);

function AttendanceDecisionRow({
  student,
  decision,
  invalid,
  lessonDetails,
  canEdit,
  canReportAbsence,
  onReportAbsence,
  onDecisionChange,
}) {
  const { t: tr } = useTranslation();
  const cancellationLabel = getCancellationLabel(decision, lessonDetails, tr);
  const studentName = getStudentName(student);
  const cancelled = isCancelledDecision(decision);

  return (
    <div className={`attendance-page__student-row ${invalid ? "attendance-page__student-row--invalid" : ""}`}>
      <div className="attendance-page__avatar">{getInitials(student)}</div>
      <div className="attendance-page__student-main">
        <div className="attendance-page__student-name">{studentName}</div>
        {student.age && <div className="attendance-page__student-meta">{student.age} {tr("manage:yearsOld_41")}</div>}
      </div>
      {lessonDetails.lessonStatus === 'cancelled' ? (
        <div className="attendance-page__badge">{tr('manage:lessonRestoration.attendanceNotApplicable')}</div>
      ) : (
        <div className="attendance-page__segments" role="group" aria-label={tr('manage:attendance.studentLabel', { name: studentName })}>
          <button
            type="button"
            className={`attendance-page__segment ${decision.decision === ATTENDANCE_DECISIONS.PRESENT ? "attendance-page__segment--selected attendance-page__segment--success" : ""}`}
            disabled={!canEdit}
            onClick={() => onDecisionChange(student.id, ATTENDANCE_DECISIONS.PRESENT)}
          >{tr("manage:present_42")}
          </button>
          <button
            type="button"
            className={`attendance-page__segment ${decision.decision === ATTENDANCE_DECISIONS.ABSENT ? "attendance-page__segment--selected attendance-page__segment--danger" : ""}`}
            disabled={!canEdit || cancelled}
            onClick={() => onDecisionChange(student.id, ATTENDANCE_DECISIONS.ABSENT)}
          >{tr("manage:absent_43")}
          </button>
          <button
            type="button"
            className={`attendance-page__segment ${isCancelledDecision(decision) ? "attendance-page__segment--selected attendance-page__segment--warning" : ""}`}
            disabled={!canReportAbsence || cancelled}
            onClick={() => onReportAbsence(student.id)}
          >{tr(cancelled ? "manage:cancelled_45" : "manage:cancelStudentParticipation")}
          </button>
        </div>
      )}
      {lessonDetails.lessonStatus !== 'cancelled' && cancellationLabel && (
        <div className="attendance-page__badge">{cancellationLabel}</div>
      )}
    </div>
  );
}

AttendanceDecisionRow.propTypes = {
  student: PropTypes.object.isRequired,
  decision: PropTypes.object.isRequired,
  invalid: PropTypes.bool,
  lessonDetails: PropTypes.object.isRequired,
  canEdit: PropTypes.bool.isRequired,
  canReportAbsence: PropTypes.bool,
  onReportAbsence: PropTypes.func,
  onDecisionChange: PropTypes.func.isRequired,
};

AttendanceDecisionRow.defaultProps = {
  canReportAbsence: false,
  onReportAbsence: () => {},
  invalid: false,
};

export default AttendanceDecisionRow;
